import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Logo } from "@/components/Logo";
import { MIN_PASSWORD_LENGTH, PASSWORD_HINT, validateNewPassword, evaluatePassword } from "@/lib/password-policy";
import { PasswordRequirements } from "@/components/auth/PasswordRequirements";
import { finalizeInvitePassword } from "@/lib/admin.functions";
import { INVITE_EXPIRED_MESSAGE, INVITE_TTL_MS } from "@/lib/admin-invite";
import {
  classifyLinkError,
  INVITE_LINK_ERROR_TITLE,
  linkErrorTitle,
} from "@/lib/invite-link-error";
import { RequestNewLinkPanel } from "@/components/auth/RequestNewLinkPanel";

import { classifyPasswordUpdateError } from "@/lib/password-update-error";
import {
  LINK_CREDENTIAL_PARAMS,
  parseInviteLink,
  type ParsedInviteLink,
} from "@/lib/invite-link";

export const Route = createFileRoute("/intern_/reset-password")({
  head: () => ({
    meta: [
      { title: "Passwort festlegen – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ResetPasswordPage,
});

type Status =
  | { kind: "loading" }
  | { kind: "confirm" }
  | { kind: "manual" }
  | { kind: "redeeming" }
  | { kind: "ready" }
  | { kind: "error"; msg: string };

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [parsed, setParsed] = useState<ParsedInviteLink | null>(null);
  const [status, setStatus] = useState<Status>({ kind: "loading" });
  const [manualCode, setManualCode] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const redeemedRef = useRef(false);

  const isInvite = parsed?.isInvite ?? true;

  // Link nur LESEN — kein Einlösen beim Rendern (Ticket K1): Mail-Scanner und
  // Weiterleitungsseiten dürfen den Einmal-Token nicht verbrauchen.
  useEffect(() => {
    const link = parseInviteLink(window.location.href);
    setParsed(link);
    setManualEmail(link.email ?? "");

    if (link.errorDescription) {
      setStatus({
        kind: "error",
        msg: classifyLinkError(decodeURIComponent(link.errorDescription)).msg,
      });
      return;
    }
    if (link.hasCredentials) {
      setStatus({ kind: "confirm" });
      return;
    }

    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      setStatus(data.session ? { kind: "ready" } : { kind: "manual" });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const stripCredentials = () => {
    const cleaned = new URL(window.location.href);
    for (const key of LINK_CREDENTIAL_PARAMS) cleaned.searchParams.delete(key);
    window.history.replaceState({}, "", cleaned.pathname + cleaned.search);
  };

  const failLink = async (rawMessage: string | null | undefined) => {
    const { msg } = classifyLinkError(rawMessage);
    // Halbfertige Session beenden, damit niemand mit gültigem Token weiter
    // navigiert, ohne ein Passwort gesetzt zu haben.
    try {
      await supabase.auth.signOut();
    } catch {
      /* ignore */
    }
    setStatus({ kind: "error", msg });
  };

  /** Einlösen — ausschließlich durch bewusste Nutzeraktion aufgerufen. */
  const redeem = async (code?: string, email?: string) => {
    if (!parsed || redeemedRef.current) return;
    redeemedRef.current = true;
    setStatus({ kind: "redeeming" });
    const otpType = parsed.authFlow === "recovery" ? "recovery" : "invite";

    try {
      if (code && email) {
        const { error } = await supabase.auth.verifyOtp({
          email,
          token: code,
          type: otpType,
        });
        if (error) throw new Error(error.message);
      } else if (parsed.tokenHash) {
        const { error } = await supabase.auth.verifyOtp({
          token_hash: parsed.tokenHash,
          type: otpType,
        });
        if (error) throw new Error(error.message);
      } else if (parsed.otpCode && parsed.email) {
        const { error } = await supabase.auth.verifyOtp({
          email: parsed.email,
          token: parsed.otpCode,
          type: otpType,
        });
        if (error) throw new Error(error.message);
      } else if (parsed.accessToken && parsed.refreshToken) {
        const { error } = await supabase.auth.setSession({
          access_token: parsed.accessToken,
          refresh_token: parsed.refreshToken,
        });
        if (error) throw new Error(error.message);
      } else if (parsed.pkceCode) {
        const { error } = await supabase.auth.exchangeCodeForSession(parsed.pkceCode);
        if (error) throw new Error(error.message);
      } else {
        throw new Error("invalid link");
      }
    } catch (err) {
      redeemedRef.current = false;
      stripCredentials();
      await failLink(err instanceof Error ? err.message : String(err));
      return;
    }

    stripCredentials();
    setStatus({ kind: "ready" });
  };

  const onManualSubmit = (e: FormEvent) => {
    e.preventDefault();
    const code = manualCode.trim();
    const email = manualEmail.trim().toLowerCase();
    if (!code || !email) {
      setFormError("Bitte E-Mail-Adresse und Code aus der E-Mail eingeben.");
      return;
    }
    setFormError(null);
    void redeem(code, email);
  };

  // Für Invite-Flow: 30-Minuten-Gültigkeit prüfen, sobald Session steht.
  useEffect(() => {
    if (!isInvite || status.kind !== "ready") return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (cancelled) return;
      const raw = (data.user?.user_metadata as Record<string, unknown> | undefined)
        ?.invited_at;
      const invitedAt = typeof raw === "string" ? Date.parse(raw) : NaN;
      if (Number.isFinite(invitedAt) && Date.now() - invitedAt > INVITE_TTL_MS) {
        await supabase.auth.signOut();
        if (cancelled) return;
        setStatus({ kind: "error", msg: INVITE_EXPIRED_MESSAGE });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isInvite, status.kind]);

  const finalizeInvite = useServerFn(finalizeInvitePassword);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const problem = validateNewPassword(password, confirm);
    if (problem) {
      setFormError(problem);
      return;
    }
    try {
      // Beide Wege laufen über den Server, damit `password_set_at` immer
      // gesetzt wird — sonst gilt ein Altbestand weiter als Neueinladung.
      await finalizeInvite({ data: { password, mode: isInvite ? "invite" : "reset" } });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg === "INVITE_EXPIRED") {
        await supabase.auth.signOut();
        setStatus({ kind: "error", msg: INVITE_EXPIRED_MESSAGE });
        return;
      }
      const mapped = classifyPasswordUpdateError(msg);
      if (mapped.requiresNewLink) {
        try {
          await supabase.auth.signOut();
        } catch {
          /* ignore */
        }
        setStatus({ kind: "error", msg: mapped.msg });
        return;
      }
      setFormError(mapped.msg);
      return;
    }

    setDone(true);
    setTimeout(() => navigate({ to: "/admin", replace: true }), 1200);
  };

  const heading = isInvite ? "Willkommen bei creategy" : "Neues Passwort";
  const intro = isInvite
    ? "Legen Sie ein sicheres Passwort fest, um Ihr internes Konto zu aktivieren."
    : "Legen Sie ein neues Passwort für Ihr Konto fest.";
  const cta = isInvite ? "Konto aktivieren" : "Passwort speichern";
  const confirmCta = isInvite ? "Konto jetzt aktivieren" : "Neues Passwort festlegen";
  const successMsg = isInvite
    ? "Konto aktiviert. Sie werden weitergeleitet…"
    : "Passwort aktualisiert. Sie werden weitergeleitet…";

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex h-16 max-w-6xl items-center px-6 md:px-12">
          <Logo />
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-3xl font-extrabold">{heading}</h1>
          <p className="mt-3 text-sm text-muted-foreground">{intro}</p>

          {(status.kind === "loading" || status.kind === "redeeming") && (
            <p className="mt-6 text-sm text-muted-foreground">Link wird geprüft…</p>
          )}

          {status.kind === "confirm" && (
            <div className="mt-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                Klicken Sie hier, um Ihr Passwort festzulegen. Ihr Link bleibt bis
                dahin unverbraucht.
              </p>
              <button
                type="button"
                onClick={() => void redeem()}
                className="w-full rounded-md bg-brand-button px-4 py-2.5 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90"
              >
                {confirmCta}
              </button>
            </div>
          )}

          {status.kind === "manual" && (
            <form onSubmit={onManualSubmit} className="mt-6 space-y-4" noValidate>
              <p className="text-sm text-muted-foreground">
                Bitte öffnen Sie den Link direkt aus Ihrer E-Mail. Alternativ können
                Sie den Code aus der E-Mail hier eingeben.
              </p>
              <div>
                <label htmlFor="manual-email" className="block text-sm font-semibold">
                  E-Mail-Adresse
                </label>
                <input
                  id="manual-email"
                  type="email"
                  autoComplete="email"
                  value={manualEmail}
                  onChange={(e) => setManualEmail(e.target.value)}
                  className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
                />
              </div>
              <div>
                <label htmlFor="manual-code" className="block text-sm font-semibold">
                  Code aus der E-Mail
                </label>
                <input
                  id="manual-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
                />
              </div>
              {formError && <p className="text-sm text-destructive">{formError}</p>}
              <button
                type="submit"
                className="w-full rounded-md bg-brand-button px-4 py-2.5 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90"
              >
                {confirmCta}
              </button>
            </form>
          )}

          {status.kind === "error" && (
            <div className="mt-6 space-y-3 rounded-md border border-border bg-secondary p-4 text-sm">
              <p className="font-semibold text-destructive">{linkErrorTitle(status.msg)}</p>
              <p className="text-muted-foreground">{status.msg}</p>
              <RequestNewLinkPanel area="intern" initialEmail={parsed?.email ?? manualEmail} />
              <Link
                to="/intern"
                className="block text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                Zur internen Anmeldung
              </Link>
            </div>
          )}


          {status.kind === "ready" && done && (
            <p className="mt-6 text-sm">{successMsg}</p>
          )}

          {status.kind === "ready" && !done && (
            <form onSubmit={onSubmit} className="mt-8 space-y-4" noValidate>
              <div>
                <label htmlFor="pw" className="block text-sm font-semibold">
                  {isInvite ? "Passwort" : "Neues Passwort"}{" "}
                  <span className="text-muted-foreground">({PASSWORD_HINT})</span>
                </label>
                <input
                  id="pw"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-describedby="password-requirements"
                  className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
                />
                <PasswordRequirements password={password} confirm={confirm} />
              </div>
              <div>
                <label htmlFor="pw2" className="block text-sm font-semibold">
                  Wiederholen
                </label>
                <input
                  id="pw2"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
                />
              </div>
              {formError && <p className="text-sm text-destructive">{formError}</p>}
              <button
                type="submit"
                disabled={!evaluatePassword(password, confirm).allMet}
                aria-describedby="password-requirements"
                className="w-full rounded-md bg-brand-button px-4 py-2.5 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {cta}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
